export type CameraFailure = 'unsupported' | 'inapp' | 'denied' | 'notfound' | 'error';

export class CameraError extends Error {
  constructor(readonly kind: CameraFailure, message: string) {
    super(message);
  }
}

/** QR を LINE や Instagram のアプリ内ブラウザで開いたケース。カメラが使えないことが多い。 */
export function isInAppBrowser(ua: string): boolean {
  return /\bLine\/|Instagram|FBAN|FBAV|MicroMessenger|Twitter/i.test(ua);
}

export function classifyCameraError(e: unknown, hasApi: boolean, ua: string): CameraFailure {
  const inApp = isInAppBrowser(ua);
  if (!hasApi) return inApp ? 'inapp' : 'unsupported';
  const name = typeof e === 'object' && e !== null && 'name' in e ? String((e as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return inApp ? 'inapp' : 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'notfound';
  return 'error';
}

function waitForDimensions(v: HTMLVideoElement, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const started = performance.now();
    const check = () => {
      if (v.videoWidth > 0 && v.videoHeight > 0) return resolve();
      if (performance.now() - started > timeoutMs) return reject(new Error('video metadata timeout'));
      requestAnimationFrame(check);
    };
    check();
  });
}

/** 背面カメラを開いて video に流す。失敗は CameraError(kind 付き)にまとめる。 */
export async function openCamera(video: HTMLVideoElement): Promise<{ width: number; height: number }> {
  const hasApi = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  let stream: MediaStream | null = null;
  try {
    if (!hasApi) throw new Error('getUserMedia unavailable');
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
    video.srcObject = stream;
    await video.play();
    await waitForDimensions(video, 10000);
    return { width: video.videoWidth, height: video.videoHeight };
  } catch (e) {
    stream?.getTracks().forEach((t) => t.stop());
    video.srcObject = null;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    throw new CameraError(classifyCameraError(e, hasApi, ua), (e as Error)?.message ?? String(e));
  }
}

export function stopCamera(video: HTMLVideoElement): void {
  const stream = video.srcObject as MediaStream | null;
  stream?.getTracks().forEach((t) => t.stop());
  video.srcObject = null;
}
